import { PlusIcon, PencilIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import { useParams } from "react-router-dom";

import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import Sidebar from "../../components/Sidebar";

import { createChecklistItem } from "../../services/createChecklistItem";
import { deleteChecklistItem } from "../../services/deleteChecklistItem";
import { GetChecklist } from "../../services/getChecklist";
import { getServiceTaskByJob } from "../../services/serviceTasks/getServiceTaskByJob";
import { updateTaskV2 } from "../../services/serviceTasks/updateTaskV2";
import { updateChecklistItem } from "../../services/updateChecklistItem";

import ChecklistFormSection from "../TaskForm/ChecklistFormSection";
import AddItemToJobModal from "./AddItemToJobModal";
import DeleteModal from "../../utils/DeleteModal";
import TypeBadge from "../../components/TypeBadge";
import { term } from "../../terms";
import { usePermissions } from "../../context/PermissionsContext";

function EditTask() {
    const { can } = usePermissions();
    const params = useParams();

    // Main states
    const [task, setTask] = useState({ name: "" });
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Checklist and modal states
    const [checklist, setCheckList] = useState([]);
    const [inputCheckList, setInputCheckList] = useState([]);
    const [openAddItem, setOpenAddItem] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [removeChecklistItemId, setRemoveChecklistItemId] = useState(null);

    // sidebar status
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const noPermissionMessage = "You don't have permission to do this.";
    const canReadPipelines =
        can("pipelines.read") || can("pipelines.write") || can("pipelines.delete");
    const canWritePipelines = can("pipelines.write");
    const canDeletePipelines = can("pipelines.delete");

    // Hoist retrieveChecklist before using it on useEffect
    const retrieveChecklist = async () => {
        const checklistRes = await GetChecklist(params.taskId);
        if (!checklistRes.success) {
            toast.error(checklistRes.message);
            return;
        }
        const arr = checklistRes.data.map((value) => ({
            id: value.id,
            description: value.description,
            links: value.links ? JSON.parse(value.links) : [],
            isChecked: false,
            editing: false,
            updated: false,
            order: value.order,
        }));
        setCheckList(arr);
    };

    // Initial load of task and checklist
    useEffect(() => {
        async function runOnLoad() {
            if (!canReadPipelines) {
                setLoading(false);
                return;
            }
            try {
                const taskRes = await getServiceTaskByJob({
                    jobId: params.jobId,
                    taskId: params.taskId,
                });
                setTask({ name: taskRes.data.name });
                await retrieveChecklist();
            } catch (err) {
                toast.error("Error cargando la tarea.");
            } finally {
                setLoading(false);
            }
        }
        runOnLoad();
    }, [params.jobId, params.taskId, canReadPipelines]);

    // Save name changes (sends description to the API)
    const saveDetails = async () => {
        if (!canWritePipelines) {
            toast.error(noPermissionMessage);
            return;
        }
        if (task.name.trim() === "") {
            toast.error("Name is required.");
            return;
        }
        setIsSaving(true);
        try {
            const res = await updateTaskV2({
                jobId: params.jobId,
                taskId: params.taskId,
                name: task.name,
            });
            if (res.data.success) {
                toast.success(res.data.message);
                setIsEditing(false);
            } else {
                toast.error(res.data.error || "Error al guardar.");
            }
        } catch (err) {
            toast.error("Error de red o servidor.");
        } finally {
            setIsSaving(false);
        }
    };

    // switch edition mode
    const toggleEdit = () => {
        if (!canWritePipelines) return;
        if (!isEditing && task.name.trim() === "") {
            toast.error("Name is required.");
            return;
        }
        setIsEditing((prev) => !prev);
    };

    // Handle checklist item deletion
    const onClickRemoveChecklistItem = (id) => {
        if (!canDeletePipelines) {
            toast.error(noPermissionMessage);
            return;
        }
        if (checklist.length === 1) {
            toast.error("At least one checklist item is required.");
            return;
        }
        setRemoveChecklistItemId(id);
        setIsDeleteModalOpen(true);
    };
    const removeChecklistItem = async () => {
        if (!canDeletePipelines) {
            toast.error(noPermissionMessage);
            return;
        }
        if (checklist.length === 1) {
            toast.error("At least one checklist item is required.");
            return;
        }
        const res = await deleteChecklistItem(params.taskId, removeChecklistItemId);
        if (res.success) {
            await retrieveChecklist();
        } else {
            toast.error("You don't have permission to do this.");
        }
        setIsDeleteModalOpen(false);
    };

    // Open add item modal
    const onClickAddItem = () => {
        if (!canWritePipelines) return;
        setInputCheckList([]);
        setOpenAddItem(true);
    };

    return (
        <section>
            {/* Modales y notificaciones */}
            <AddItemToJobModal
                open={openAddItem}
                setOpen={setOpenAddItem}
                taskId={params.taskId}
                getNewChecklistItems={retrieveChecklist}
            />
            <DeleteModal
                isModalOpen={isDeleteModalOpen}
                setIsModalOpen={setIsDeleteModalOpen}
                onRemove={removeChecklistItem}
            />
            <Toaster />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />

            <div className="flex h-screen overflow-hidden">
                {/* Sidebar */}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                {/* Main content */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <PageContainer
                        isLoading={loading}
                        loadingText={`Loading ${term("step_singular").toLowerCase()}...`}
                    >
                        <main>
                            <div className="px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-2xl">
                                    <Breadcrumbs
                                        home="/templates/jobs"
                                        pages={[
                                            {
                                                name: `${term("pipeline_singular")}`,
                                                href: `/templates/job/${params.jobId}`,
                                                current: true,
                                            },
                                            {
                                                name: `Edit ${term("step_singular")}`,
                                                href: `/templates/jobs/${params.jobId}/task/${params.taskId}`,
                                                current: true,
                                            },
                                        ]}
                                    />

                                    <h1 className="p-4 text-2xl font-bold md:text-3xl text-slate-800">
                                        Edit {term("step_singular")}
                                    </h1>

                                    {!canReadPipelines && (
                                        <div className="w-full p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                            You don't have permission to view pipelines.
                                        </div>
                                    )}
                                    {/* Name section */}
                                    <div className="w-full pb-4 bg-white border rounded-sm shadow-lg border-slate-200">
                                        <div className="px-5 py-4 space-y-5">
                                            <div className="flex justify-end">
                                                <span
                                                    data-tip={
                                                        canWritePipelines
                                                            ? "Edit"
                                                            : noPermissionMessage
                                                    }
                                                    className="inline-flex"
                                                >
                                                    <button
                                                        onClick={toggleEdit}
                                                        disabled={!canWritePipelines}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium border rounded-sm shadow-sm ${
                                                            canWritePipelines
                                                                ? ""
                                                                : "opacity-50 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        {isEditing ? (
                                                            "Cancel"
                                                        ) : (
                                                            <>
                                                                <PencilIcon className="w-4 h-4 mr-1" />
                                                                Edit Name
                                                            </>
                                                        )}
                                                    </button>
                                                </span>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Name
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        disabled={
                                                            !isEditing ||
                                                            !canWritePipelines
                                                        }
                                                        type="text"
                                                        value={task.name}
                                                        onChange={(e) =>
                                                            setTask({
                                                                ...task,
                                                                name: e.target.value,
                                                            })
                                                        }
                                                        className={`w-full shadow-sm border rounded-sm p-2 ${
                                                            !isEditing &&
                                                            "bg-gray-100"
                                                        }`}
                                                    />
                                                </div>
                                                {isEditing && (
                                                    <div className="mt-4">
                                                        <span
                                                            data-tip={
                                                                canWritePipelines
                                                                    ? "Save"
                                                                    : noPermissionMessage
                                                            }
                                                            className="inline-flex"
                                                        >
                                                            <button
                                                                onClick={saveDetails}
                                                                disabled={
                                                                    isSaving ||
                                                                    !canWritePipelines
                                                                }
                                                                className={`inline-flex items-center px-4 py-2 border rounded-sm shadow-sm text-sm font-medium text-white ${
                                                                    isSaving ||
                                                                    !canWritePipelines
                                                                        ? "bg-teal-300 cursor-not-allowed"
                                                                        : "bg-teal-500 hover:bg-teal-600"
                                                                }`}
                                                            >
                                                                {isSaving && (
                                                                    <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                                                                )}
                                                                {isSaving
                                                                    ? "Saving..."
                                                                    : "Save"}
                                                            </button>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Checklist section */}
                                    <div className="w-full mt-12 bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                <TypeBadge type="checklist" />
                                            </h2>
                                            <span
                                                data-tip={
                                                    canWritePipelines
                                                        ? "Add item"
                                                        : noPermissionMessage
                                                }
                                                className="inline-flex"
                                            >
                                                <button
                                                    onClick={onClickAddItem}
                                                    disabled={!canWritePipelines}
                                                    className={`inline-flex items-center p-1 text-white bg-teal-500 border border-transparent rounded-sm shadow-sm ${
                                                        canWritePipelines
                                                            ? "hover:bg-teal-600"
                                                            : "opacity-50 cursor-not-allowed"
                                                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500`}
                                                >
                                                    <PlusIcon className="w-5 h-5" />
                                                </button>
                                            </span>
                                        </header>

                                        <ChecklistFormSection
                                            taskId={params.taskId}
                                            checklist={checklist}
                                            inputCheckList={inputCheckList}
                                            retrieveChecklist={retrieveChecklist}
                                            setCheckList={setCheckList}
                                            setInputCheckList={setInputCheckList}
                                            updateChecklistItem={updateChecklistItem}
                                            onClickRemoveChecklistItem={
                                                onClickRemoveChecklistItem
                                            }
                                            createChecklistItem={createChecklistItem}
                                        />
                                    </div>
                                </div>
                            </div>
                        </main>
                    </PageContainer>
                </div>
            </div>
        </section>
    );
}

export default EditTask;
